import { OrganizationUnitStatus } from '@today/types'
import type { OrgUnitDTO } from '@/services/organization.service'
import {
	collect,
	dateToIso,
	diff,
	type FieldErrors,
	isoToDate,
	optionalCountry,
	optionalText,
	parseMetadata,
	requiredText,
	textOrNull,
	validityError,
} from './form-rules'

/** The unit form as edited (TO-389 organization-admin-ui REQ-005). Dates are YYYY-MM-DD; metadata is JSON text. */
export interface UnitFormValues {
	code: string
	name: string
	typeCode: string
	parentCode: string
	shortName: string
	externalId: string
	status: OrganizationUnitStatus
	isPhysical: boolean
	institutionCode: string
	countryCode: string
	timezone: string
	validFrom: string
	validTo: string
	metadata: string
}

export const EMPTY_UNIT_FORM: UnitFormValues = {
	code: '',
	name: '',
	typeCode: '',
	parentCode: '',
	shortName: '',
	externalId: '',
	status: OrganizationUnitStatus.ACTIVE,
	isPhysical: false,
	institutionCode: '',
	countryCode: '',
	timezone: '',
	validFrom: '',
	validTo: '',
	metadata: '',
}

export const unitFormErrors = (v: UnitFormValues): FieldErrors =>
	collect({
		code: requiredText(v.code, 64),
		name: requiredText(v.name, 200),
		typeCode: v.typeCode === '' ? 'validation.required' : null,
		shortName: optionalText(v.shortName, 100),
		externalId: optionalText(v.externalId, 128),
		institutionCode: optionalText(v.institutionCode, 32),
		countryCode: optionalCountry(v.countryCode),
		timezone: optionalText(v.timezone, 64),
		metadata: parseMetadata(v.metadata) === null ? 'validation.metadata' : null,
		validTo: validityError(v.validFrom, v.validTo),
	})

/** The mutable fields as the API wants them (no code/parentCode). */
const mutableBody = (v: UnitFormValues): Record<string, unknown> => ({
	name: v.name.trim(),
	typeCode: v.typeCode,
	shortName: textOrNull(v.shortName),
	externalId: textOrNull(v.externalId),
	status: v.status,
	isPhysical: v.isPhysical,
	institutionCode: textOrNull(v.institutionCode),
	countryCode: textOrNull(v.countryCode),
	timezone: textOrNull(v.timezone),
	validFrom: dateToIso(v.validFrom),
	validTo: dateToIso(v.validTo),
	metadata: parseMetadata(v.metadata) ?? {},
})

/** The create body: required fields plus every optional field the user set away from its default. */
export const buildUnitCreateBody = (v: UnitFormValues): Record<string, unknown> => {
	const optional = diff(mutableBody(EMPTY_UNIT_FORM), mutableBody(v))
	const { name: _name, typeCode: _type, ...rest } = optional
	return {
		code: v.code.trim(),
		name: v.name.trim(),
		typeCode: v.typeCode,
		...(v.parentCode ? { parentCode: v.parentCode } : {}),
		...rest,
	}
}

export const unitToForm = (u: OrgUnitDTO): UnitFormValues => ({
	code: u.code,
	name: u.name,
	typeCode: u.typeCode,
	parentCode: u.parentCode ?? '',
	shortName: u.shortName ?? '',
	externalId: u.externalId ?? '',
	status: u.status,
	isPhysical: u.isPhysical,
	institutionCode: u.institutionCode ?? '',
	countryCode: u.countryCode ?? '',
	timezone: u.timezone ?? '',
	validFrom: isoToDate(u.validFrom),
	validTo: isoToDate(u.validTo),
	metadata: Object.keys(u.metadata).length === 0 ? '' : JSON.stringify(u.metadata, null, 2),
})

/** Only the changed mutable fields; `code` and `parentCode` are never sent (moves use REQ-006). */
export const buildUnitPatch = (original: OrgUnitDTO, v: UnitFormValues): Record<string, unknown> =>
	diff(mutableBody(unitToForm(original)), mutableBody(v))
